import { FC, memo, useState, useRef, useEffect, useCallback, BaseSyntheticEvent } from 'react';

import { Option } from 'interfaces';
import { InputElementBase } from '../../models/input-element-base';
import { Error } from '../Error/Error';

interface SearchableSelectProps extends InputElementBase {
  options: Option[];
  selectedValue: string | number;
}

export const SearchableSelect: FC<SearchableSelectProps> = memo(
  ({
    name,
    wrapperClassname = '',
    options,
    onChange,
    selectedValue,
    required,
    disabled,
    error,
    label,
    labelClass,
  }) => {
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const selectedOption = options.find(
      (o) => o.value !== '' && String(o.value) === String(selectedValue),
    );
    const displayValue = isOpen ? search : (selectedOption?.name ?? '');

    const filteredOptions = options.filter(
      (o) =>
        o.value !== '' &&
        String(o.name).toLowerCase().includes(search.toLowerCase()),
    );

    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(e.target as Node)
        ) {
          setIsOpen(false);
          setSearch('');
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = useCallback(
      (option: Option) => {
        const event = {
          target: { name, value: String(option.value) },
        };
        onChange?.(event as unknown as BaseSyntheticEvent);
        setIsOpen(false);
        setSearch('');
      },
      [name, onChange],
    );

    return (
      <div className={`${wrapperClassname} mb-2`} ref={containerRef}>
        <div
          className={`focus-label ${error ? 'error' : ''}`}
          style={{ position: 'relative' }}
        >
          {label && (
            <label
              htmlFor={`${name}-search`}
              className={`form-label ${labelClass ?? ''} ${required ? 'required' : ''}`}
            >
              {label}
            </label>
          )}
          <input type="hidden" name={name} value={String(selectedValue)} />
          <div style={{ position: 'relative' }}>
            <input
              id={`${name}-search`}
              type="text"
              className={`form-control${error ? ' is-invalid' : ''}`}
              value={displayValue}
              onChange={(e) => {
                setSearch(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => {
                setSearch('');
                setIsOpen(true);
              }}
              disabled={disabled}
              placeholder={`Search ${label ?? ''}…`}
              autoComplete="off"
              aria-autocomplete="list"
              aria-expanded={isOpen}
            />
            <span
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: '#6c757d',
                fontSize: '11px',
              }}
            >
              {isOpen ? '▲' : '▼'}
            </span>
          </div>

          {isOpen && (
            <div
              role="listbox"
              style={{
                position: 'absolute',
                top: 'calc(100% - 4px)',
                left: 0,
                right: 0,
                zIndex: 1050,
                maxHeight: '220px',
                overflowY: 'auto',
                border: '1px solid #dee2e6',
                borderRadius: '0 0 0.375rem 0.375rem',
                backgroundColor: '#fff',
                boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
              }}
            >
              {filteredOptions.length === 0 ? (
                <div
                  style={{
                    padding: '10px 14px',
                    color: '#6c757d',
                    fontSize: '14px',
                  }}
                >
                  No results found
                </div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected =
                    String(option.value) === String(selectedValue);
                  return (
                    <div
                      key={String(option.value)}
                      role="option"
                      aria-selected={isSelected}
                      onMouseDown={() => handleSelect(option)}
                      style={{
                        padding: '10px 14px',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#e9ecef' : 'transparent',
                        fontWeight: isSelected ? 600 : 'normal',
                        fontSize: '14px',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected)
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = isSelected
                          ? '#e9ecef'
                          : 'transparent';
                      }}
                    >
                      {option.name}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {error && <Error message={error} />}
        </div>
      </div>
    );
  },
);
